<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import FormSwitch from 'v3/components/Form/Switch.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import { mapGetters as mapAccountGetters } from 'vuex';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    FormSwitch,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
      selectedPriorityItem: null,
      chatbotOptions: [
        { value: 'enabled', label: this.$t('CHATBOT.OPTIONS.ENABLED') },
        { value: 'disabled', label: this.$t('CHATBOT.OPTIONS.DISABLED') },
      ],
      pendingToggleValue: false,
      draftOrderInfo: null,
    };
  },
  computed: {
    ...mapAccountGetters({
      getAccount: 'accounts/getAccount',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    selectedChatbotOption() {
      return this.aiBotEnabled ? 'enabled' : 'disabled';
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      currentAccountId: 'getCurrentAccountId',
      inboxGetter: 'inboxes/getInbox',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    accountId() {
      return this.currentAccountId || this.currentUser?.account_id;
    },
    canUseAiCore() {
      if (!this.accountId) return false;
      return this.isFeatureEnabledonAccount(this.accountId, 'ai_core');
    },
    currentInbox() {
      if (!this.currentChat?.inbox_id || !this.inboxGetter) {
        return null;
      }
      return this.inboxGetter(this.currentChat.inbox_id);
    },
    isInboxBotEnabled() {
      const isBot = this.currentInbox?.is_bot;
      if (typeof isBot === 'boolean') {
        return isBot;
      }
      return true;
    },
    canToggleChatbot() {
      return this.canUseAiCore && this.isInboxBotEnabled;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
          });
      },
    },
    assignedPriority() {
      const selectedOption = this.priorityOptions.find(
        opt => opt.id === this.currentChat.priority
      );
      return selectedOption || this.priorityOptions[0];
    },
    aiBotEnabled() {
      return this.currentChat?.chatbot_enabled || false;
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  
  watch: {
    currentChat: {
      handler(newChat, oldChat) {
        if (newChat && newChat.id !== oldChat?.id) {
          this.assignedAgent = null;
          this.assignedTeam = null;
          this.selectedPriorityItem = this.priorityOptions.find(
            opt => opt.id === newChat?.priority
          ) || this.priorityOptions[0];
        }
      },
      immediate: true,
      deep: true
    }
  },
  
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const conversationId = this.currentChat.id;
      const currentPriority = this.currentChat?.priority ?? null;
      const nextPriority = selectedPriorityItem ? selectedPriorityItem.id : null;

      this.selectedPriorityItem = selectedPriorityItem || this.priorityOptions[0];

      if (currentPriority === nextPriority) {
        return;
      }

      this.$store.dispatch('setCurrentChatPriority', {
        priority: nextPriority,
        conversationId,
      });

      this.$store
        .dispatch('assignPriority', { conversationId, priority: nextPriority })
        .then(() => {
          useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            oldValue: currentPriority,
            newValue: nextPriority,
            from: 'Conversation Sidebar',
          });
          const priorityName = selectedPriorityItem
            ? selectedPriorityItem.name
            : this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE');
          useAlert(
            this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
              priority: priorityName,
              conversationId,
            })
          );
        });
    },
    async toggleChatbot(isEnabled) {
      if (!this.canToggleChatbot) {
        return;
      }
      const conversationId = this.currentChat.id;
      const inboxId = this.currentChat.inbox_id;
      const contactId = this.currentChat.meta.contact_inbox.source_id;
      const accountId = this.accountId;
      
      try {
        await this.$store.dispatch('toggleChatbot', { 
          conversationId, 
          enabled: isEnabled,
          inboxId,
          contactId,
          accountId
        });
        this.currentChat.chatbot_enabled = isEnabled;
      } catch (error) {
        console.error('Failed to update chatbot status:', error);
        // Không cập nhật local state khi có lỗi
        // Error notification đã được hiển thị trong store action
      }
    },
    onCustomerFound(customerData) {
      // Thông tin khách hàng đã được lưu vào conversation attributes bởi API
      useAlert(this.$t ? this.$t('CONVERSATION.CUSTOMER.FOUND', { name: customerData.FullName }) : `Đã tìm thấy khách hàng: ${customerData.FullName}`, { type: 'success' });
    },
    onCustomerNotFound(error) {
      useAlert(this.$t ? this.$t('CONVERSATION.CUSTOMER.NOT_FOUND', { error }) : `Không tìm thấy khách hàng: ${error}`, { type: 'error' });
    },
    onCustomerError(error) {
      useAlert(error || (this.$t ? this.$t('CONVERSATION.CUSTOMER.ERROR') : 'Đã xảy ra lỗi'), { type: 'error' });
    },
    openAiOrderForm() {
      if (!this.canUseAiCore) {
        return;
      }
      emitter.emit('OPEN_AI_ORDER_FORM_MODAL', { conversationId: this.conversationId });
    },
  },
};
</script>

<template>
  <div class="space-y-4">
    <!-- Assignee Section -->
    <div class="space-y-2 pt-2">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      />
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')"
        :input-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')"
        @select="onClickAssignAgent"
      />
      <woot-button
        v-if="showSelfAssign"
        icon="arrow-right"
        variant="link"
        size="small"
        @click="onSelfAssign"
      >
        {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
      </woot-button>
    </div>

    <!-- Team Section -->
    <div class="space-y-2">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')"
        :input-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.INPUT')"
        @select="onClickAssignTeam"
      />
    </div>

    <!-- Priority Section -->
    <div class="space-y-2">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="selectedPriorityItem || assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')"
        :no-search-result="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')"
        :input-placeholder="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')"
        @select="onClickAssignPriority"
      />
    </div>

    <!-- Chatbot Section -->
    <div v-if="canUseAiCore" class="space-y-2">
      <ContactDetailsItem compact :title="$t('CONVERSATION.CHATBOT.TITLE')" />
      <div class="flex items-center justify-between">
        <FormSwitch
          :model-value="aiBotEnabled"
          :disabled="!canToggleChatbot"
          :title="!isInboxBotEnabled ? $t('CONVERSATION.CHATBOT.DISABLED_IN_INBOX_TOOLTIP') : ''"
          @update:model-value="toggleChatbot"
        />
      </div>
      <p
        v-if="!isInboxBotEnabled"
        class="text-xs text-ash-600"
      >
        {{ $t('CONVERSATION.CHATBOT.DISABLED_IN_INBOX_NOTE') }}
      </p>
    </div>

    <!-- Labels Section -->
    <div class="space-y-2">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
      />
      <ConversationLabels :conversation-id="conversationId" />
    </div>

    <!-- AI Order Button -->
    <!-- TODO: uncomment khi bật tính năng order -->
    <!-- <div v-if="canUseAiCore" class="pt-2">
      <woot-button size="small" icon="add-circle" @click="openAiOrderForm">
        {{ $t('AI_SETTINGS.GENERAL.CREATE_ORDER') }}
      </woot-button>
    </div> -->

    <!-- Modal moved to ConversationView.vue -->
  </div>
</template>
<style scoped>
/* Style for the toggle */
.peer-checked:translate-x-5 {
  transform: translateX(20px);
}
</style>
